import React, { useState } from "react";
import { Pressable, StyleSheet, View, Text } from "react-native";
import type { PressableStateCallbackType } from "react-native";
import { ListChecks, Note } from "@phosphor-icons/react";
import { colors, radii, spacing, webTransitions } from "../../../styles/design-system";

export type ActivityType = "task" | "assignment";

export interface ActivityCardProps {
  id?: string;
  type?: ActivityType;
  title: string;
  contextLabel?: string; // e.g. course name
  metaLabel?: string; // e.g. Due Friday, Nov 24
  isSelected?: boolean;
  disabled?: boolean;
  onPress?: () => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({
  type = "task",
  title,
  contextLabel,
  metaLabel,
  isSelected = false,
  disabled = false,
  onPress,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handlePress = () => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  const renderIcon = () => {
    if (type === "assignment") {
      return <Note size={18} color={colors.brand[500]} weight="bold" />;
    }
    return <ListChecks size={18} color={colors.brand[500]} weight="bold" />;
  };

  const cardStyle = (state: PressableStateCallbackType) => {
    const pressed = state.pressed;
    const active = isSelected || (isHovered && !pressed);

    return [styles.cardBase, active && styles.cardActive, disabled && styles.cardDisabled];
  };

  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={cardStyle}>
      <View style={styles.iconContainer}>{renderIcon()}</View>
      <View style={styles.textContainer}>
        <Text style={styles.title} numberOfLines={1}>
          {title}
        </Text>
        {contextLabel || metaLabel ? (
          <View style={styles.metaRow}>
            {contextLabel ? (
              <Text style={styles.metaText} numberOfLines={1}>
                {contextLabel}
              </Text>
            ) : null}
            {contextLabel && metaLabel ? <Text style={styles.dot}>·</Text> : null}
            {metaLabel ? (
              <Text style={styles.metaText} numberOfLines={1}>
                {metaLabel}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  cardBase: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    marginBottom: spacing.xs,
    ...webTransitions.fast,
  },
  cardActive: {
    backgroundColor: colors.brand[25],
    borderColor: colors.brand[500],
  },
  cardDisabled: {
    opacity: 0.5,
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
    marginRight: spacing.sm,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: "500",
    color: colors.grey[900],
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 2,
  },
  metaText: {
    fontSize: 12,
    color: colors.grey[600],
  },
  dot: {
    marginHorizontal: 4,
    fontSize: 12,
    color: colors.grey[400],
  },
});

export default ActivityCard;
